import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { IAppConfig } from './app.config.model';
import { Observable } from 'rxjs';
import { environment } from '../environments/environment';
import { Network } from '@capacitor/network';
import { AlertController } from '@ionic/angular';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { AuthApiService } from './shared/services/auth-api.service';
import { CrewSelfService } from './shared/services/crew-self.service';

@Injectable()
export class AppConfig {
  public static settings: IAppConfig;
  private networkListener: any;
  private isNetworkListenerAttached: boolean = false;

  constructor(
    private readonly http: HttpClient,
    private readonly alertController: AlertController,
    private readonly authApiService: AuthApiService,
    private readonly crewSelfService: CrewSelfService
  ) { }

  async presentNetworkAlert() {
    this.isNetworkListenerAttached = true;
    console.log('AppConfig: Showing No Internet alert...');
    const alert = await this.alertController.create({
      header: 'No Internet',
      message: 'Please check your network connection.',
      backdropDismiss: false,
      buttons: [
        {
          text: 'Retry',
          role: 'reload',
          handler: async () => {
            console.log('Retry clicked. Reloading app...');
            const status = await Network.getStatus();
            if (status.connected) {
              location.reload();
              return true
            }
            else {
              return false
            }
          }
        }
      ]
    });

    await alert.present();
  }

  async checkAppUpdate(): Promise<boolean> {
    const appInfo = await App.getInfo();
    const currentVersion = appInfo.version;

    return new Promise((resolve) => {
      this.authApiService.appUpdateCheckApi().subscribe({
        next: async (response: any) => {
          console.log("====res")
          console.log(response)
          const platform = Capacitor.getPlatform();
          const storeVersion = response?.[platform]?.latestVersion;

          if (this.isNewerVersion(storeVersion, currentVersion)) {
            const updateUrl = platform === "ios"?response?.[platform]?.IOS_APP_STORE_URL: response?.[platform]?.ANDROID_APP_PLAY_STORE_URL;
            const userConfirmed = await this.showUpdateAlert(updateUrl);
            resolve(userConfirmed); // Only resolve true if user chose to update or skipped
          } else {
            resolve(true); // No update required
          }
        },
        error: (err: any) => {
          console.error('AppConfig: Error while checking app update:', err);
          resolve(true); // Let app continue even if update check fails
        },
      });
    });
  }

  async showUpdateAlert(appStoreUrl: string): Promise<boolean> {
    const platform = Capacitor.getPlatform();
    const alertHeader = platform === 'ios' ? AppConfig.settings?.IOS_NEW_APP_VERSION_UPDATE_ALERT_TITLE : AppConfig.settings?.ANDROID_NEW_APP_VERSION_UPDATE_ALERT_TITLE;
    const alertMessage = platform === 'ios' ? AppConfig.settings?.IOS_NEW_APP_VERSION_UPDATE_ALERT_MESSAGE : AppConfig.settings?.ANDROID_NEW_APP_VERSION_UPDATE_ALERT_MESSAGE;

    return new Promise(async (resolve) => {
      const alert = await this.alertController.create({
        header: alertHeader || 'Update Available',
        message: alertMessage || 'A new version of the app is available. Please update to the latest version.',
        backdropDismiss: false,
        buttons: [
          {
            text: 'Update',
            handler: () => {
              window.open(appStoreUrl, '_system');
              resolve(false); // Don't proceed with app load
            }
          }          
        ]
      });

      await alert.present();
    });
  }

  isNewerVersion(storeVersion: string, currentVersion: string): boolean {
    const sv = storeVersion.split('.').map(Number);
    const cv = currentVersion.split('.').map(Number);
    for (let i = 0; i < Math.max(sv.length, cv.length); i++) {
      const s = sv[i] || 0;
      const c = cv[i] || 0;
      if (s > c) return true;
      if (s < c) return false;
    }
    return false;
  }

  async load(): Promise<void> {
    try {
      const status = await Network.getStatus();
      console.log('AppConfig: Initial network status =>', status);

      if (!status.connected) {
        console.log('AppConfig: Device is offline, registering network listener...');
        if (!this.isNetworkListenerAttached) {
          await this.presentNetworkAlert();
        }
        return; 
      }
      return new Promise<void>((resolve, reject) => {
        this.fetchConfigurations().subscribe({
          next: async (response: IAppConfig[] | any) => {
            console.log('AppConfig: Configuration fetched successfully:', response);
            AppConfig.settings = <IAppConfig>response.data;
            AppConfig.settings.MAINTENANCE_ALERT_ON = false;
            this.removeNetworkListener();

            // check for maintenance mode   
            if(AppConfig.settings?.MAINTENANCE_MODE_ENABLED){
              await this.crewSelfService.showMaintenanceAlert();
              return;
            }

            // check for mobile app version update
            if (Capacitor.getPlatform() === 'ios' || Capacitor.getPlatform() === 'android') {
              const canProceed = await this.checkAppUpdate();
              if (!canProceed) {
                console.warn('AppConfig: Update required. Halting app initialization.');
                return;
              }
            }
            resolve();
          },
          error: (err: any) => {
            console.error('AppConfig: Failed to fetch configuration:', err);
            reject(err instanceof Error ? err : new Error(JSON.stringify(err)));
          }
        });
      });
    } catch (error: any) {
      console.error('AppConfig: Error in load():', error);
      return Promise.reject(error instanceof Error ? error : new Error(JSON.stringify(error)));
    }
  }

  fetchConfigurations(): Observable<IAppConfig> {
    console.log('AppConfig: Calling fetchConfigurations()...');
    return this.http.get<IAppConfig>(`${environment.CREW_APP_BACKEND_API_BASE_URL}/client-configuration`);
  }

  removeNetworkListener() {
    if (this.networkListener) {
      console.log('AppConfig: Removing network listener');
      this.networkListener.remove();
      this.networkListener = null;
    }
  }
}
